import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useScrollAndFocus } from '@/components/ux/hooks/use-scroll-and-focus';
import { cn } from '@/lib/utils';

import EmojiReactions from '@/components/ui/emojis/emoji-reactions.tsx';
import EmojiSelector from '@/components/ui/emojis/emoji-selector.tsx';
import type { Reply, Trade } from '@/models/registry';
import { EmojiReactionType } from '@/models/types.ts';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import MessageTipTap from '../../deal/activity-feed/tip-tap-editor/message-tiptap';
import CommentReplyHeader from './comment-reply-header';
import EditTradeReplyTiptap from './tip-tap-editor/edit-trade-reply-tiptap';
import TradeReplyMenu from './trade-reply-menu';

const TradeReply = observer(
  ({
    reply,
    tradeId,
    tradeShortId,
  }: {
    reply: Reply;
    tradeId: Trade['id'];
    tradeShortId: Trade['short_id'];
  }) => {
    const { content } = reply;

    const { shouldShowFocus, focusRef } = useScrollAndFocus({
      uiType: 'reply',
      id: reply.id,
    });

    const replyHoverRef = useRef<HTMLDivElement>(null);

    const [isEditing, setIsEditing] = useState(false);

    return (
      <CardHeader
        className={cn('border-b p-4', {
          'border-primary border': shouldShowFocus,
        })}
        ref={(el) => {
          focusRef.current = el;
          replyHoverRef.current = el;
        }}
      >
        <CardTitle className="flex flex-row justify-between p-0 pt-0 pb-0">
          <CommentReplyHeader
            commentOrReply={reply}
            isEditingComment={isEditing}
          />
          <div className="flex flex-row">
            <EmojiSelector
              targetId={reply.id}
              hoverRef={replyHoverRef}
              targetType={EmojiReactionType.Reply}
            />
            <TradeReplyMenu
              reply={reply}
              tradeShortId={tradeShortId}
              hoverRef={focusRef}
              setIsEditingReply={setIsEditing}
            />
          </div>
        </CardTitle>
        {isEditing ? (
          <EditTradeReplyTiptap
            tradeId={tradeId}
            reply={reply}
            setIsEditingReply={setIsEditing}
            className="pl-6"
          />
        ) : (
          <CardDescription className="pl-6 whitespace-pre-wrap text-uneditable">
            <MessageTipTap content={content} />
          </CardDescription>
        )}
        {reply.emoji_reactions.length > 0 && (
          <EmojiReactions
            targetId={reply.id}
            targetType={EmojiReactionType.Reply}
            className="min-h-6"
            reactionButtonClassName="h-6 text-xs leading-none px-2"
            selectorButtonClassName="h-6 w-6 p-0 rounded-md leading-none"
          />
        )}
      </CardHeader>
    );
  },
);

export default TradeReply;
